<script setup>
import { ref, shallowRef, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import emojis from './emojisGroup.json';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useRecentEmojis } from 'shared/composables/useRecentEmojis';

const props = defineProps({
  onClick: {
    type: Function,
    default: () => {},
  },
  showRemoveButton: {
    type: Boolean,
    default: false,
  },
});

const SEARCH_KEY = 'Search';
const RECENT_KEY = 'Recent';

const { t, te, locale } = useI18n();
const { recentEmojis, addRecentEmoji } = useRecentEmojis();

const selectedKey = ref(recentEmojis.value.length ? RECENT_KEY : SEARCH_KEY);
const search = ref('');
const searchbar = ref(null);
const emojiItem = ref(null);
// Portuguese names and keywords (Unicode CLDR), loaded only when the app is in Portuguese.
const portugueseSearchIndex = shallowRef(null);

// Not every locale has these strings, so fall back to the English ones instead of showing a key.
const translate = (key, fallback) => (te(key) ? t(key) : fallback);

const normalize = text =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const emojiKey = emoji => emoji.replace('\uFE0F', '');

const searchableText = item =>
  portugueseSearchIndex.value
    ? portugueseSearchIndex.value[emojiKey(item.emoji)] || ''
    : item.slug.replaceAll('_', ' ').toLowerCase();

const searchTerms = computed(() =>
  normalize(search.value).split(/\s+/).filter(Boolean)
);

const filteredCategories = computed(() =>
  emojis.map(category => ({
    ...category,
    emojis: category.emojis.filter(item =>
      searchTerms.value.every(term => searchableText(item).includes(term))
    ),
  }))
);

const categoryTitle = category =>
  translate(`EMOJI.CATEGORIES.${category.slug.toUpperCase()}`, category.name);

const selectedCategory = computed(() =>
  emojis.find(category => category.name === selectedKey.value)
);

const isRecentView = computed(
  () => selectedKey.value === RECENT_KEY && search.value === ''
);

const isCategoryView = computed(
  () => selectedKey.value !== SEARCH_KEY && search.value === ''
);

const hasEmptySearchResult = computed(() =>
  filteredCategories.value.every(category => category.emojis.length === 0)
);

const focusSearchInput = () => nextTick(() => searchbar.value?.focus());

const changeCategory = key => {
  search.value = '';
  emojiItem.value?.scrollTo({ top: 0 });
  selectedKey.value = key;
};

const getFirstEmojiByCategoryName = categoryName =>
  emojis.find(category => category.name === categoryName)?.emojis[0].emoji ||
  '';

const selectEmoji = emoji => {
  addRecentEmoji(emoji);
  props.onClick(emoji);
};

watch(search, () => {
  selectedKey.value = SEARCH_KEY;
});

watch(selectedKey, key => {
  if (key === SEARCH_KEY) focusSearchInput();
});

onMounted(async () => {
  focusSearchInput();
  if (locale.value.toLowerCase().startsWith('pt')) {
    portugueseSearchIndex.value = (
      await import('./emojisSearchPt.json')
    ).default;
  }
});
</script>

<template>
  <div
    role="dialog"
    class="emoji-dialog bg-n-background shadow-lg rounded-md outline outline-1 outline-n-weak box-content h-[18.75rem] absolute right-0 -top-[95px] w-80 z-20"
  >
    <div class="flex flex-col">
      <div class="flex gap-2 m-2 sticky top-2">
        <input
          ref="searchbar"
          v-model="search"
          type="text"
          class="focus:box-shadow-blue dark:focus:box-shadow-dark !mb-0 !h-8 !text-sm"
          :placeholder="$t('EMOJI.PLACEHOLDER')"
        />
        <NextButton
          v-if="showRemoveButton"
          faded
          sm
          slate
          class="flex-shrink-0"
          :label="$t('EMOJI.REMOVE')"
          @click="onClick('')"
        />
      </div>
      <div v-if="isRecentView" ref="emojiItem" class="emoji-item">
        <h5
          class="text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
        >
          {{ translate('EMOJI.RECENT', 'Recently used') }}
        </h5>
        <div v-if="recentEmojis.length" class="emoji--row">
          <button
            v-for="emoji in recentEmojis"
            :key="emoji"
            v-dompurify-html="emoji"
            class="emoji--item"
            @click="selectEmoji(emoji)"
          />
        </div>
        <p v-else class="px-2 py-4 m-0 text-sm text-n-slate-11">
          {{ translate('EMOJI.RECENT_EMPTY', 'No recently used emojis yet') }}
        </p>
      </div>
      <div v-else-if="isCategoryView" ref="emojiItem" class="emoji-item">
        <h5
          class="text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
        >
          {{ categoryTitle(selectedCategory) }}
        </h5>
        <div class="emoji--row">
          <button
            v-for="item in selectedCategory.emojis"
            :key="item.slug"
            v-dompurify-html="item.emoji"
            class="emoji--item"
            @click="selectEmoji(item.emoji)"
          />
        </div>
      </div>
      <div v-else ref="emojiItem" class="emoji-item">
        <div v-for="category in filteredCategories" :key="category.slug">
          <h5
            v-if="category.emojis.length > 0"
            class="text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
          >
            {{ categoryTitle(category) }}
          </h5>
          <div v-if="category.emojis.length > 0" class="emoji--row">
            <button
              v-for="item in category.emojis"
              :key="item.slug"
              v-dompurify-html="item.emoji"
              class="emoji--item"
              @click="selectEmoji(item.emoji)"
            />
          </div>
        </div>
        <div
          v-if="hasEmptySearchResult"
          class="items-center flex flex-col h-[13.25rem] justify-center"
        >
          <div class="text-n-slate-11 mb-2">
            <FluentIcon icon="emoji" size="48" />
          </div>
          <span class="text-n-slate-11 text-sm font-medium">
            {{ $t('EMOJI.NOT_FOUND') }}
          </span>
        </div>
      </div>

      <div
        class="emoji-dialog--footer relative w-full py-0 rounded-b-[0.34rem] px-1 bg-n-slate-3"
        role="menu"
      >
        <ul
          class="flex relative left-[2px] rtl:left-[unset] rtl:right-[2px] list-none m-0 overflow-auto py-1 px-0"
        >
          <li @click="changeCategory(SEARCH_KEY)">
            <button
              class="emoji--item"
              :class="{ active: selectedKey === SEARCH_KEY }"
            >
              <FluentIcon icon="search" size="16" class="text-n-slate-11" />
            </button>
          </li>
          <li @click="changeCategory(RECENT_KEY)">
            <button
              class="emoji--item"
              :class="{ active: selectedKey === RECENT_KEY }"
              :title="translate('EMOJI.RECENT', 'Recently used')"
            >
              <Icon icon="i-lucide-clock" class="size-4 text-n-slate-11" />
            </button>
          </li>
          <li
            v-for="category in emojis"
            :key="category.slug"
            @click="changeCategory(category.name)"
          >
            <button
              v-dompurify-html="getFirstEmojiByCategoryName(category.name)"
              class="emoji--item"
              :class="{ active: selectedKey === category.name }"
              :title="categoryTitle(category)"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
@tailwind components;

@layer components {
  .box-shadow-blue {
    box-shadow:
      0 0 0 1px #1f93ff,
      0 0 1px 2px #c7e3ff;
  }

  .box-shadow-dark {
    box-shadow:
      0 0 0 1px #212222,
      0 0 1px 2px #4c5155;
  }
}
</style>

<style lang="scss">
.emoji-dialog {
  &::before {
    @apply absolute -bottom-3 h-3 w-6 bg-n-slate-3 content-[""];
    clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
  }
}

.emoji--item {
  @apply bg-transparent border-0 rounded cursor-pointer text-lg h-6 m-0 py-0 px-1 hover:bg-n-slate-4;
}

.emoji--row {
  @apply box-border p-1;

  .emoji--item {
    @apply h-[1.625rem] w-[1.625rem] leading-normal m-1;
  }
}

.emoji-item {
  @apply h-[13.25rem] overflow-y-auto;
}

.emoji-dialog--footer {
  ul {
    > li {
      @apply items-center cursor-pointer flex justify-center p-1;
    }

    li .active {
      @apply bg-n-background;
    }

    .emoji--item {
      @apply items-center flex text-sm;

      &:hover {
        @apply bg-n-slate-2;
      }
    }
  }
}
</style>
